import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const PROFILE_API = `${API_ROOT}/api/profile`;
const CITIES_API = `${API_ROOT}/api/cities`;
const ALLOWED_MODULES = [1, 3, 7, 13, 16, 19];
const MAX_IMAGE_UPLOAD_BYTES = 200 * 1024;

const initialForm = {
  NameTitleID: "",
  Name: "",
  FNameTitleID: "",
  FatherName: "",
  MNameTitleID: "",
  MotherName: "",
  Gender: "",
  DOB: "",
  DOR: "",
  BloodGroupID: "",
  QualificationID: "",
  ReligionID: "",
  CasteCategoryID: "",
  Email: "",
  AEmail: "",
  EmgMobile: "",
  AMobile: "",
  PAN: "",
  Aadhar: "",
  PermanentAddress: "",
  PermanentStateID: "",
  PermanentCityID: "",
  PermanentPIN: "",
  DOJ: "",
  DOC: "",
  ECRNo: "",
  MeritRank: "1",
  BHDepartmentID: "",
  PropertyID: "",
  EResolverID: "",
  AResolverID: "",
  PFNo: ""
};

function toInputDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

async function compressImageToMaxSize(file, maxBytes) {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = objectUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(image, 0, 0);
    const toBlobWithQuality = (nextQuality) => new Promise((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", nextQuality);
    });

    let quality = 0.9;
    let blob = await toBlobWithQuality(quality);
    if (!blob) {
      throw new Error("Failed to process image.");
    }

    while (blob.size > maxBytes && quality > 0.3) {
      quality -= 0.1;
      blob = await toBlobWithQuality(quality);
      if (!blob) {
        throw new Error("Failed to process image.");
      }
    }

    while (blob.size > maxBytes && canvas.width > 600) {
      canvas.width = Math.round(canvas.width * 0.9);
      canvas.height = Math.round(canvas.height * 0.9);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      blob = await toBlobWithQuality(quality);
      if (!blob) {
        throw new Error("Failed to process image.");
      }
    }

    const baseName = file.name.replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function normalizeEmployeeToForm(employee) {
  return {
    ...initialForm,
    NameTitleID: employee?.NameTitleID || "",
    Name: employee?.EmpName || employee?.Name || "",
    FNameTitleID: employee?.FNameTitleID || "",
    FatherName: employee?.FatherName || "",
    MNameTitleID: employee?.MNameTitleID || "",
    MotherName: employee?.MotherName || "",
    Gender: employee?.Gender || "",
    DOB: toInputDate(employee?.DOB),
    DOR: toInputDate(employee?.DOR),
    BloodGroupID: employee?.BloodGroupID || "",
    QualificationID: employee?.QualificationID || "",
    ReligionID: employee?.ReligionID || "",
    CasteCategoryID: employee?.CasteCategoryID || "",
    Email: employee?.Email || "",
    AEmail: employee?.AEmail || "",
    EmgMobile: employee?.EmgMobile || "",
    AMobile: employee?.AMobile || "",
    PAN: employee?.PAN || "",
    Aadhar: employee?.Aadhar || "",
    PermanentAddress: employee?.PermanentAddress || "",
    PermanentStateID: employee?.PermanentStateID || "",
    PermanentCityID: employee?.PermanentCityID || "",
    PermanentPIN: employee?.PermanentPIN || "",
    DOJ: toInputDate(employee?.DOJ),
    DOC: toInputDate(employee?.DOC),
    ECRNo: employee?.ECRNo || "",
    MeritRank: employee?.MeritRank || "1",
    BHDepartmentID: employee?.BudgetDepartmentID || "",
    PropertyID: employee?.PropertyID || "",
    EResolverID: employee?.EResolverID || "",
    AResolverID: employee?.AResolverID || "",
    PFNo: employee?.PFNo || ""
  };
}

export default function EditProfile() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [vehicles, setVehicles] = useState(["", "", "", "", ""]);
  const [options, setOptions] = useState({
    nameTitles: [],
    fatherTitles: [],
    motherTitles: [],
    designations: [],
    departments: [],
    bloodGroups: [],
    states: [],
    qualifications: [],
    religions: [],
    casteCategories: [],
    properties: [],
    resolvers: []
  });
  const [cities, setCities] = useState([]);
  const [supportingDocFile, setSupportingDocFile] = useState(null);
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [activeTab, setActiveTab] = useState("personal");
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const localUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const moduleId = parseInt(localUser?.moduleId, 10);

  const loadCities = async (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }
    const res = await fetch(`${CITIES_API}?stateId=${stateId}`);
    const data = await res.json();
    if (res.ok) {
      setCities(data);
    } else {
      setCities([]);
    }
  };

  useEffect(() => {
    async function load() {
      if (!localUser?.id || Number.isNaN(moduleId)) {
        setError("Missing user session. Please login again.");
        setLoading(false);
        return;
      }
      if (!ALLOWED_MODULES.includes(moduleId)) {
        setError("Unauthorized access Or Session Timed out.");
        setLoading(false);
        return;
      }

      try {
        const [profileRes, optionsRes] = await Promise.all([
          fetch(`${PROFILE_API}/edit/${localUser.id}?moduleId=${moduleId}`),
          fetch(`${PROFILE_API}/edit/options?moduleId=${moduleId}&empType=${localUser?.etype || ""}`)
        ]);

        const profileData = await profileRes.json();
        const optionsData = await optionsRes.json();

        if (!profileRes.ok) {
          throw new Error(profileData.message || "Failed to load profile");
        }
        if (!optionsRes.ok) {
          throw new Error(optionsData.message || "Failed to load options");
        }

        const emp = profileData.employee || {};
        setForm(normalizeEmployeeToForm(emp));
        setVehicles(profileData.vehicles || ["", "", "", "", ""]);
        setMeta({
          EmployeeID: emp.EmployeeID,
          PFNo: emp.PFNo,
          Mobile: emp.Mobile,
          Country: emp.Country,
          Status: emp.Status,
          EmpType: emp.EmpType,
          JobType: emp.JobType,
          DesignationID: emp.DesignationID,
          DepartmentID: emp.DepartmentID,
          LockFields: emp.LockFields
        });
        setOptions(optionsData);

        if (emp.PermanentStateID) {
          await loadCities(emp.PermanentStateID);
        }
      } catch (err) {
        setError(err.message || "Failed to load edit profile");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [localUser?.etype, localUser?.id, moduleId]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onStateChange = async (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, PermanentStateID: value, PermanentCityID: "" }));
    await loadCities(value);
  };

  const onVehicleChange = (index, value) => {
    setVehicles((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const onSupportingDocChange = async (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setSupportingDocFile(null);
      return;
    }

    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["jpg", "jpeg", "png"].includes(ext)) {
      setError("Only JPG, JPEG, PNG files are allowed for Supporting Doc.");
      setSupportingDocFile(null);
      e.target.value = "";
      return;
    }

    try {
      setError("");
      const processed = await compressImageToMaxSize(file, MAX_IMAGE_UPLOAD_BYTES);
      setSupportingDocFile(processed);
    } catch (err) {
      setSupportingDocFile(null);
      setError(err.message || "Failed to process Supporting Doc.");
    } finally {
      e.target.value = "";
    }
  };

  const onProfilePicChange = async (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setProfilePicFile(null);
      return;
    }

    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["jpg", "jpeg", "png"].includes(ext)) {
      setError("Only JPG, JPEG, PNG files are allowed for Profile Pic.");
      setProfilePicFile(null);
      e.target.value = "";
      return;
    }

    try {
      setError("");
      const processed = await compressImageToMaxSize(file, MAX_IMAGE_UPLOAD_BYTES);
      setProfilePicFile(processed);
    } catch (err) {
      setProfilePicFile(null);
      setError(err.message || "Failed to process Profile Pic.");
    } finally {
      e.target.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (meta?.LockFields === "Y") {
      alert("This employee profile is locked. Updates not allowed.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const payload = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        payload.append(k, v ?? "");
      });
      payload.append("deptid", localUser?.deptid || "");
      vehicles.forEach((v, i) => payload.append(`Vehicle${i + 1}`, (v || "").trim()));

      if (supportingDocFile) payload.append("SupportingDoc", supportingDocFile);
      if (profilePicFile) payload.append("ProfilePic", profilePicFile);

      const res = await fetch(`${PROFILE_API}/edit/${localUser.id}?moduleId=${moduleId}`, {
        method: "POST",
        body: payload
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to update profile");
      }

      alert(data.message || "Record updated successfully.");
      navigate("/profile");
    } catch (err) {
      setError(err.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <div className="container-fluid" style={{ textAlign: "left", marginTop: "10px" }}>
      <h3 className="ctitle mb-3">Edit Profile</h3>

      <div className="mb-3 d-flex gap-2 flex-wrap">
        <button type="button" className={`btn ${activeTab === "personal" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setActiveTab("personal")}>Personal Profile</button>
        <button type="button" className={`btn ${activeTab === "official" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setActiveTab("official")}>Official Info</button>
        <button type="button" className={`btn ${activeTab === "vehicles" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setActiveTab("vehicles")}>Vehicle Details</button>
      </div>

      <form onSubmit={handleSubmit}>
        {activeTab === "personal" && (
          <div className="card p-3 mb-3">
            <div className="row g-3">
              <div className="col-md-2">
                <label className="form-label">Title</label>
                <select className="form-select" name="NameTitleID" value={form.NameTitleID} onChange={onChange}>
                  <option value="">Select</option>
                  {options.nameTitles.map((x) => <option key={x.NameTitleID} value={x.NameTitleID}>{x.NameTitle}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Emp Name</label>
                <input className="form-control" name="Name" value={form.Name} onChange={onChange} readOnly />
              </div>
              <div className="col-md-2">
                <label className="form-label">Gender</label>
                <select className="form-select" name="Gender" value={form.Gender} onChange={onChange}>
                  <option value="">Select</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                  <option value="T">Transgender</option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">DOB</label>
                <input type="date" className="form-control" name="DOB" value={form.DOB} onChange={onChange} />
              </div>
              <div className="col-md-2">
                <label className="form-label">Valid Upto</label>
                <input type="date" className="form-control" name="DOR" value={form.DOR} onChange={onChange} />
              </div>

              <div className="col-md-2">
                <label className="form-label">Father Title</label>
                <select className="form-select" name="FNameTitleID" value={form.FNameTitleID} onChange={onChange}>
                  <option value="">Select</option>
                  {options.fatherTitles.map((x) => <option key={x.NameTitleID} value={x.NameTitleID}>{x.NameTitle}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Father Name</label>
                <input className="form-control" name="FatherName" value={form.FatherName} onChange={onChange} />
              </div>
              <div className="col-md-2">
                <label className="form-label">Mother Title</label>
                <select className="form-select" name="MNameTitleID" value={form.MNameTitleID} onChange={onChange}>
                  <option value="">Select</option>
                  {options.motherTitles.map((x) => <option key={x.NameTitleID} value={x.NameTitleID}>{x.NameTitle}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Mother Name</label>
                <input className="form-control" name="MotherName" value={form.MotherName} onChange={onChange} />
              </div>

              <div className="col-md-2">
                <label className="form-label">Qualification</label>
                <select className="form-select" name="QualificationID" value={form.QualificationID} onChange={onChange}>
                  <option value="">Select</option>
                  {options.qualifications.map((x) => <option key={x.QualificationID} value={x.QualificationID}>{x.Qualification}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">Religion</label>
                <select className="form-select" name="ReligionID" value={form.ReligionID} onChange={onChange}>
                  <option value="">Select</option>
                  {options.religions.map((x) => <option key={x.ReligionID} value={x.ReligionID}>{x.Religion}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">Caste Category</label>
                <select className="form-select" name="CasteCategoryID" value={form.CasteCategoryID} onChange={onChange}>
                  <option value="">Select</option>
                  {options.casteCategories.map((x) => <option key={x.CasteCategoryID} value={x.CasteCategoryID}>{x.CasteCategory}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">Blood Group</label>
                <select className="form-select" name="BloodGroupID" value={form.BloodGroupID} onChange={onChange}>
                  <option value="">Select</option>
                  {options.bloodGroups.map((x) => <option key={x.BloodGroupID} value={x.BloodGroupID}>{x.BloodGroup}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">Emergency Mobile</label>
                <input className="form-control" name="EmgMobile" value={form.EmgMobile} onChange={onChange} maxLength={10} />
              </div>
              <div className="col-md-2">
                <label className="form-label">Alternate Mobile</label>
                <input className="form-control" name="AMobile" value={form.AMobile} onChange={onChange} maxLength={10} />
              </div>

              <div className="col-md-2">
                <label className="form-label">Email</label>
                <input type="email" className="form-control" name="Email" value={form.Email} onChange={onChange} />
              </div>
              <div className="col-md-2">
                <label className="form-label">Alternate Email</label>
                <input type="email" className="form-control" name="AEmail" value={form.AEmail} onChange={onChange} />
              </div>
              <div className="col-md-2">
                <label className="form-label">Mobile</label>
                <input className="form-control" value={`${meta?.Country || ""}${meta?.Mobile || ""}`} readOnly />
              </div>
              <div className="col-md-2">
                <label className="form-label">Status</label>
                <input className="form-control" value={meta?.Status || ""} readOnly />
              </div>

              <div className="col-md-2">
                <label className="form-label">PAN</label>
                <input className="form-control" name="PAN" value={form.PAN} onChange={onChange} maxLength={10} />
              </div>
              <div className="col-md-2">
                <label className="form-label">Aadhar</label>
                <input className="form-control" name="Aadhar" value={form.Aadhar} onChange={onChange} maxLength={12} />
              </div>

              <div className="col-md-6">
                <label className="form-label">Permanent Address</label>
                <textarea
                  className="form-control"
                  rows="1"
                  style={{ minHeight: "38px" }}
                  name="PermanentAddress"
                  value={form.PermanentAddress}
                  onChange={onChange}
                ></textarea>
              </div>
              <div className="col-md-2">
                <label className="form-label">PIN</label>
                <input className="form-control" name="PermanentPIN" value={form.PermanentPIN} onChange={onChange} maxLength={6} />
              </div>
              <div className="col-md-2">
                <label className="form-label">State</label>
                <select className="form-select" name="PermanentStateID" value={form.PermanentStateID} onChange={onStateChange}>
                  <option value="">Select</option>
                  {options.states.map((x) => <option key={x.StateID} value={x.StateID}>{x.State}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">City</label>
                <select className="form-select" name="PermanentCityID" value={form.PermanentCityID} onChange={onChange}>
                  <option value="">Select</option>
                  {cities.map((x) => <option key={x.CityID} value={x.CityID}>{x.City}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}

        {activeTab === "official" && (
          <div className="card p-3 mb-3">
            <div className="row g-3">
              <div className="col-md-2">
                <label className="form-label">PF No</label>
                <input className="form-control" name="PFNo" value={form.PFNo} readOnly />
              </div>
              <div className="col-md-2">
                <label className="form-label">Emp Type</label>
                <input className="form-control" value={meta?.EmpType || ""} readOnly />
              </div>
              <div className="col-md-2">
                <label className="form-label">Job Type</label>
                <input className="form-control" value={meta?.JobType || ""} readOnly />
              </div>
              <div className="col-md-3">
                <label className="form-label">Designation</label>
                <select className="form-select" value={meta?.DesignationID || ""} disabled>
                  <option value="">Select</option>
                  {options.designations.map((x) => <option key={x.DesignationID} value={x.DesignationID}>{x.Designation}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Department</label>
                <select className="form-select" value={meta?.DepartmentID || ""} disabled>
                  <option value="">Select</option>
                  {options.departments.map((x) => <option key={x.DepartmentID} value={x.DepartmentID}>{x.Department}</option>)}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">Budget Head Department</label>
                <select className="form-select" name="BHDepartmentID" value={form.BHDepartmentID} onChange={onChange}>
                  <option value="">Select</option>
                  {options.departments.map((x) => <option key={x.DepartmentID} value={x.DepartmentID}>{x.Department}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">EResolver</label>
                <select className="form-select" name="EResolverID" value={form.EResolverID} onChange={onChange}>
                  <option value="">Select</option>
                  {options.resolvers.map((x) => <option key={x.ResolverID} value={x.ResolverID}>{x.OfficialName}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">AResolver</label>
                <select className="form-select" name="AResolverID" value={form.AResolverID} onChange={onChange}>
                  <option value="">Select</option>
                  {options.resolvers.map((x) => <option key={x.ResolverID} value={x.ResolverID}>{x.OfficialName}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Property</label>
                <select className="form-select" name="PropertyID" value={form.PropertyID} onChange={onChange}>
                  <option value="">Select</option>
                  {options.properties.map((x) => <option key={x.PropertyID} value={x.PropertyID}>{x.PropertyName}</option>)}
                </select>
              </div>

              <div className="col-md-2">
                <label className="form-label">DOJ</label>
                <input type="date" className="form-control" name="DOJ" value={form.DOJ} onChange={onChange} />
              </div>
              <div className="col-md-2">
                <label className="form-label">DOC</label>
                <input type="date" className="form-control" name="DOC" value={form.DOC} onChange={onChange} />
              </div>
              <div className="col-md-2">
                <label className="form-label">ECR No</label>
                <input className="form-control" name="ECRNo" value={form.ECRNo} onChange={onChange} />
              </div>
              <div className="col-md-2">
                <label className="form-label">Merit Rank</label>
                <input type="number" className="form-control" name="MeritRank" value={form.MeritRank} onChange={onChange} min="1" />
              </div>
              <div className="col-md-3">
                <label className="form-label">Supporting Doc (JPG/JPEG/PNG, &lt;200KB)</label>
                <input type="file" className="form-control" accept=".jpg,.jpeg,.png" onChange={onSupportingDocChange} />
              </div>
              <div className="col-md-3">
                <label className="form-label">Profile Pic (JPG/JPEG/PNG, &lt;200KB)</label>
                <input type="file" className="form-control" accept=".jpg,.jpeg,.png" onChange={onProfilePicChange} />
              </div>
            </div>
          </div>
        )}

        {activeTab === "vehicles" && (
          <div className="card p-3 mb-3">
            <div className="row g-3">
              {[0, 1, 2, 3, 4].map((i) => (
                <div className="col-md-6" key={i}>
                  <label className="form-label">Vehicle {i + 1}</label>
                  <input className="form-control" value={vehicles[i] || ""} onChange={(e) => onVehicleChange(i, e.target.value)} maxLength={13} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="d-flex gap-2 mb-4">
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : "Submit"}</button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate("/profile")}>Cancel</button>
        </div>
      </form>
    </div>
  );
}
